import Cookies from 'js-cookie';
import { CONFIG } from '@/config/Config';

/**
 * Normalizes a value by extracting the first part before the pipe
 * and removing whitespace
 */
const normalizeId = (value: string | null | undefined): string | undefined => {
  const normalized = value?.split('|')[0]?.trim();
  return normalized ?? undefined;
};

/**
 * Extracts an identifier (uni_id, account_id, user_id, etc) from multiple sources in priority order:
 * 1. Cookie
 * 2. Configuration (CONFIG.cookieOrParam)
 * 3. Query string
 *
 * Returns the default value (DEFAULT by default) if no source provides a valid value.
 *
 * @param configKey - The configuration key to search for (will not come as a parameter during execution)
 * @param defaultValue - Default value if nothing is found (default: 'DEFAULT')
 * @returns The normalized identifier or the default value
 */
export const extractCookieId = (configKey: string, defaultValue: string = 'DEFAULT'): string => {
  try {
    // Try to retrieve from cookie
    const cookieValue = normalizeId(Cookies.get(configKey) ?? undefined);
    if (cookieValue) {
      return cookieValue;
    }

    // Try to retrieve from configuration
    const configValue = normalizeId(CONFIG.cookieOrParam(configKey) ?? undefined);
    if (configValue) {
      return configValue;
    }

    // Try to retrieve from query string
    if (globalThis.window !== undefined) {
      const queryValue = normalizeId(
        new URLSearchParams(globalThis.window.location.search).get(configKey) ?? undefined
      );
      if (queryValue) {
        return queryValue;
      }
    }
  } catch {
    // Ignore errors and return default value
  }

  return defaultValue;
};

/**
 * Retrieves the uni_id using the generic extractCookieId function
 * @returns The normalized uni_id or 'DEFAULT'
 */
export const getUniId = (): string => extractCookieId(CONFIG.AUTH.UNI_ID);

/**
 * Retrieves the account_id using the generic extractCookieId function
 * @returns The normalized account_id or 'DEFAULT'
 */
export const getAccountId = (): string => extractCookieId(CONFIG.AUTH.ACC_ID);

/**
 * Retrieves the user_id using the generic extractCookieId function
 * @returns The normalized user_id or 'DEFAULT'
 */
export const getUserId = (): string => extractCookieId(CONFIG.AUTH.USER_ID);